import React, { useCallback, useEffect, useMemo } from 'react'
import { TransportAndAccessSection } from '../../../TransportAndAccessSection'
import { StaticTransportServices } from '../../../TransportAndAccessSection/StaticTransportServices'
import { useAnalysisMode } from '../../contexts/AnalysisModeContext'
import { useAnalysisServices } from '../../contexts/AnalysisServicesContext'
import { useAnalysisMapSectionData } from '../../contexts/MapSectionDataContext'

/**
 * Shared TransportAccessTab used by both main and public apps.
 *
 * - Main app (readOnly=false): gets real transport services
 *   from getSectionData('transportServices')
 * - Public app (readOnly=true): creates StaticTransportServices
 *   from pre-fetched entity.transport data
 */
export const TransportAccessTab: React.FC = () => {
  const { entity, readOnly } = useAnalysisMode()
  const { getSectionData } = useAnalysisServices()
  const { setActiveSection, updateSectionData, clearSectionData } =
    useAnalysisMapSectionData()

  useEffect(() => {
    setActiveSection('transport')
    return () => {
      clearSectionData()
    }
  }, [setActiveSection])

  // Sync transport data to map section context
  const handleMapDataChange = useCallback(
    (data: {
      publicTransportStops: any[]
      isochrones: Record<string, any> | null
      activeMobilityMetrics: any
      roadAccessMetrics: any
      services: any
    }) => {
      updateSectionData({
        transport: {
          publicTransportStops: data.publicTransportStops,
          isochrones: data.isochrones,
          activeMobilityMetrics: data.activeMobilityMetrics,
          roadAccessMetrics: data.roadAccessMetrics,
          services: data.services,
        },
      })
    },
    [updateSectionData]
  )

  const services = useMemo(() => {
    if (readOnly) {
      const transportData = entity?.transport || entity?.transportAccess || null
      return new StaticTransportServices(transportData)
    }
    return getSectionData('transportServices')
  }, [readOnly, entity, getSectionData])

  // Build entity with geo_center fallback
  const entityWithGeoCenter = useMemo(() => {
    if (!entity) return null
    if (entity.geo_center) return entity
    // Fallback: use transport coordinates if available
    const transport = entity.transport || entity.transportAccess
    if (transport?.coordinates) {
      return { ...entity, geo_center: transport.coordinates }
    }
    return entity
  }, [entity])

  if (!services || !entityWithGeoCenter) return null

  return (
    <TransportAndAccessSection
      entity={entityWithGeoCenter}
      services={services}
      onMapDataChange={handleMapDataChange}
    />
  )
}
